
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Plus, RefreshCw, List, Star, Image } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { RegionSelector } from "@/components/admin/services/RegionSelector";
import { ServiceTable } from "@/components/admin/services/ServiceTable";
import { DisplayOrderTable } from "@/components/admin/services/DisplayOrderTable";
import { RegionManagement } from "@/components/admin/services/RegionManagement";
import { ServiceImageMigrationTool } from "@/components/admin/services/ServiceImageMigrationTool";
import { useRegions } from "@/hooks/useRegions";
import { useRegionCurrency } from "@/hooks/useRegionCurrency";
import { useAdminServices } from "@/hooks/useAdminServices";
import { useAdminCheckQuery } from "@/hooks/useAdminCheckQuery";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminServices = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedRegion, setSelectedRegion] = useState<string>("nairobi");
  const [isRegionDialogOpen, setIsRegionDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("services");
  const { data: isAdmin } = useAdminCheckQuery();

  const { data: regions, refetch: refetchRegions } = useRegions();
  const { data: regionCurrency } = useRegionCurrency(selectedRegion);
  const { data: services, refetch: refetchServices } = useAdminServices(selectedRegion);

  useEffect(() => {
    const handleFocus = () => {
      refetchServices();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetchServices]);

  const handleTogglePublished = async (id: number, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('services')
        .update({ published: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      refetchServices();
      toast({
        title: "Success",
        description: `Service ${!currentStatus ? 'published' : 'unpublished'} successfully`,
        variant: "success",
      });
    } catch (error) {
      console.error('Error toggling published status:', error);
      toast({
        title: "Error",
        description: "Failed to update service status",
        variant: "destructive",
      });
    }
  };

  const handleToggleFeatured = async (id: number, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('services')
        .update({ featured: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      refetchServices();
      toast({
        title: "Success",
        description: `Service ${!currentStatus ? 'featured' : 'unfeatured'} successfully`,
        variant: "success",
      });
    } catch (error) {
      console.error('Error toggling featured status:', error);
      toast({
        title: "Error",
        description: "Failed to update featured status",
        variant: "destructive",
      });
    }
  };

  const handleRefresh = () => {
    refetchServices();
    toast({
      title: "Refreshed",
      description: "Service list has been refreshed",
      variant: "success",
    });
  };

  return (
    <AdminLayout title="Manage Services">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <RegionSelector 
              selectedRegion={selectedRegion} 
              regions={regions} 
              onRegionChange={setSelectedRegion} 
            />
            <Button variant="outline" size="icon" onClick={handleRefresh} title="Refresh services">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => navigate("/admin/services/new")}>
            <Plus className="w-4 h-4 mr-2" />
            Add New Service
          </Button>
        </div>
      </div>

      <Tabs defaultValue="services" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="services" className="flex items-center gap-2">
            <List className="w-4 h-4" />
            Services
          </TabsTrigger>
          <TabsTrigger value="display-order" className="flex items-center gap-2">
            <Star className="w-4 h-4" />
            Display Order
          </TabsTrigger>
          <TabsTrigger value="images" className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            Image Tools
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="services" className="space-y-4">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <ServiceTable 
              services={services || []} 
              onTogglePublished={handleTogglePublished}
              onToggleFeatured={handleToggleFeatured}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="display-order" className="space-y-4">
          <DisplayOrderTable 
            services={services || []} 
            regionCurrency={regionCurrency || 'Ksh'} 
            refetchServices={refetchServices}
          />
        </TabsContent>

        <TabsContent value="images" className="space-y-4">
          <ServiceImageMigrationTool />
        </TabsContent>
      </Tabs>

      {isAdmin && (
        <RegionManagement 
          regions={regions} 
          refetchRegions={refetchRegions} 
          isOpen={isRegionDialogOpen} 
          setIsOpen={setIsRegionDialogOpen}
        />
      )}
    </AdminLayout>
  );
};

export default AdminServices;
